// Turning on notifications from the introduction: web push in the browser,
// the native permission + device token inside the Android app.

import { api } from '../api';
import { native } from '../native';
import { i18n } from '../i18n.svelte';

export type PushStatus = 'unsupported' | 'default' | 'granted' | 'denied';
export type EnableResult = 'enabled' | 'denied' | 'unsupported' | 'failed';

function webSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

export function pushStatus(): PushStatus {
  if (native.isNativeApp()) {
    const s = native.getNotificationPermissionStatus();
    if (s === 'granted') return 'granted';
    if (s === 'denied' || s === 'blocked') return 'denied';
    if (s === 'unsupported') return 'unsupported';
    return 'default';
  }
  if (!webSupported()) return 'unsupported';
  return Notification.permission === 'default' ? 'default' : Notification.permission;
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function serviceWorker(timeoutMs = 5000): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  if (existing) return existing;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs))
  ]);
}

async function enableNative(): Promise<EnableResult> {
  const answer = await native.requestNotificationPermission().catch(() => 'denied');
  if (answer !== 'granted') return answer === 'unsupported' ? 'unsupported' : 'denied';
  const token = await native.getDeviceToken();
  await api.post('/api/notification-tokens', {
    token,
    platform: native.platform(),
    app_version: native.appVersion(),
    locale: i18n.lang,
    device_model: ''
  });
  return 'enabled';
}

async function enableWeb(): Promise<EnableResult> {
  if (!webSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'failed';
  const registration = await serviceWorker();
  // Without a service worker (development) permission alone is all we can do.
  if (!registration) return 'enabled';
  const { publicKey } = await api.get<{ publicKey: string }>('/api/push/vapid-key');
  if (!publicKey) return 'failed';
  const subscription =
    (await registration.pushManager.getSubscription()) ||
    (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) }));
  await api.post('/api/push/subscribe', subscription.toJSON());
  return 'enabled';
}

/** Ask for permission and register this device for pushes. */
export async function enableNotifications(): Promise<EnableResult> {
  try {
    return native.isNativeApp() ? await enableNative() : await enableWeb();
  } catch {
    return 'failed';
  }
}
